// PostHog for the public site. Mirrors the trade portal's setup (portal repo,
// src/lib/analytics/posthog.ts) so both report into the same EU project with the
// same guarantees: cookieless persistence, no session recording, and redaction of
// anything token-shaped in a URL.
//
// Without REACT_APP_POSTHOG_KEY, PostHog never loads — which is what we want in
// development and in tests, so local browsing does not land in production data.
import posthog from 'posthog-js';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let started = false;

/** Strip token/code query values before they reach an event property. */
function redactTokens(url: string): string {
  return url.replace(/([?&](?:token|code|ref)=)[^&#]*/gi, '$1redacted');
}

const URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$pathname',
];

export function startPostHog(): void {
  if (started || !KEY || process.env.NODE_ENV === 'test') return;
  started = true;

  posthog.init(KEY, {
    api_host: HOST,
    // Cookieless: no consent banner to chase, and nothing to leak between devices.
    persistence: 'memory',
    disable_session_recording: true,
    capture_pageview: false, // sent per route change below, not per document load
    capture_performance: { web_vitals: true },
    capture_dead_clicks: true,
    autocapture: true,
    before_send: (event) => {
      if (!event) return null;
      if (event.properties) {
        for (const key of URL_PROPERTIES) {
          const value = event.properties[key];
          if (typeof value === 'string') event.properties[key] = redactTokens(value);
        }
      }
      return event;
    },
  });
}

/** One pageview per route, with the quote step or product code kept in the path. */
export function capturePageview(pathname: string, search = ''): void {
  if (!started) return;
  posthog.capture('$pageview', {
    $current_url: redactTokens(window.location.href),
    path: pathname,
    search: redactTokens(search),
  });
}

export type SiteEventName =
  | 'quote_step_viewed'
  | 'quote_submitted'
  | 'product_quick_look'
  | 'added_to_quote'
  | 'contact_modal_opened';

export function track(name: SiteEventName, props: Record<string, unknown> = {}): void {
  if (!started) return;
  posthog.capture(name, props);
}
